/**
 * Size Category Compatibility Functions
 * Handles aircraft size-based spot filtering and compatibility checks
 */

import { ParkingRequest, Spot } from "./types";

/**
 * Check if an aircraft can use a spot based on size policy
 *
 * Uses array-based policy where spots explicitly list which aircraft sizes they allow.
 * This enables flexible policies like:
 * - Large-only spots (prevent waste): ["large"]
 * - Heavy aircraft only: ["super-midsize", "large"]
 * - Common jets: ["small", "midsize"]
 * - Universal spots: ["very-small", "small", "midsize", "super-midsize", "large"]
 * - Non-contiguous ranges: ["very-small", "large"] (skip middle sizes)
 *
 * @param aircraft - Aircraft to place
 * @param spot - Spot to check
 * @returns true if aircraft size is in spot's allowedSizes array
 *
 * @example
 * ```typescript
 * const spot = { allowedSizes: ["small", "midsize"] };
 * const smallAircraft = { sizeCategory: "small" };
 * const largeAircraft = { sizeCategory: "large" };
 *
 * canAircraftUseSpot(smallAircraft, spot) // true
 * canAircraftUseSpot(largeAircraft, spot) // false (policy blocks)
 * ```
 */
export function canAircraftUseSpot(
  aircraft: ParkingRequest,
  spot: Spot
): boolean {
  return spot.allowedSizes.includes(aircraft.sizeCategory);
}
